import { useEffect, useMemo, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from 'react-apollo';
import { useLocation, useRouteMatch } from 'react-router-dom';
import qs from 'query-string';
import pmap from 'p-map-browser';

import { AccountCardsPhotoDownloadCreate } from '../../../../../../graphql/mutations/photo-downloads';
import { getAlbumFolderPrintAssetBackgrounds } from '../helpers';
import { HierarchyItemForPrintAssets } from '../../../../../../graphql/queries/hierarchy-item';
import { PhotoForStandaloneView } from '../../../../../../graphql/queries/photos';
import uuidv4 from '../../../../../../util/uuid';

const useAccountDownloadsConfigurator = () => {
  const client = useApolloClient();
  const match = useRouteMatch();
  const location = useLocation();
  const { photoIds = [], identityId } = qs.parse(location.search, {
    arrayFormat: 'bracket',
  });
  const [activeAccountCardConfiguration, setActiveAccountCardConfiguration] =
    useState();
  const [submitting, setSubmitting] = useState(false);
  const [accountCardConfigurations, setAccountCardConfigurations] = useState(
    [],
  );
  const [loadingPhotos, setLoadingPhotos] = useState(true);
  const [fetchedPhotos, setFetchedPhotos] = useState([]);
  const [fetchedPhotosBool, setFetchedPhotosBool] = useState(false);
  const [activePhoto, setActivePhoto] = useState();

  const { data: { hierarchyItem } = {}, loading: loadingHierarchyItem } =
    useQuery(HierarchyItemForPrintAssets, {
      variables: {
        id: match?.params?.hierarchyItemId,
      },
      skip: !match?.params?.hierarchyItemId,
    });

  const [accountCardsPhotoDownloadCreate] = useMutation(
    AccountCardsPhotoDownloadCreate,
  );

  useEffect(() => {
    const fetchPhotos = async () => {
      const photoIdsPhotos = [];
      await pmap(
        photoIds,
        async photoId => {
          try {
            const { data: { photo } = {} } = await client.query({
              query: PhotoForStandaloneView,
              variables: {
                id: photoId,
              },
              fetchPolicy: 'cache-first',
            });

            photoIdsPhotos.push({ ...photo, uuid: uuidv4() });
          } catch {
            // Ignore errors and assume photos were not found.
          }
        },
        { concurrency: 3 },
      );
      setFetchedPhotos(photoIdsPhotos);
      setActivePhoto(photoIdsPhotos[0]);
      setLoadingPhotos(false);
      setFetchedPhotosBool(true);
    };
    if (photoIds.length && !fetchedPhotosBool) {
      fetchPhotos();
    } else {
      setLoadingPhotos(false);
    }
  }, [photoIds]);

  const albumFolder = useMemo(
    () =>
      hierarchyItem?.album_folder?.root ??
      hierarchyItem?.album?.root_album_folder,
    [hierarchyItem],
  );

  const onAccountCardsPhotoDownloadCreate = async emailAddress => {
    const result = await accountCardsPhotoDownloadCreate({
      variables: {
        input: {
          name: 'Digital Downloads',
          email_address: emailAddress,
          account_card_ids: accountCardConfigurations.map(
            ({ accountCard }) => accountCard.id,
          ),
          album_folder_id: albumFolder?.id,
        },
      },
    });

    return result;
  };

  const printAssetBackgrounds =
    getAlbumFolderPrintAssetBackgrounds(albumFolder);

  return {
    identityId,
    albumFolder,
    hierarchyItem,
    loadingAlbumFolder: loadingHierarchyItem,
    onAccountCardsPhotoDownloadCreate,
    printAssetBackgrounds,
    accountCardConfigurations,
    setAccountCardConfigurations,
    activeAccountCardConfiguration,
    setActiveAccountCardConfiguration,
    activePhoto,
    setActivePhoto,
    loadingPhotos,
    fetchedPhotos,
    setFetchedPhotos,
    submitting,
    setSubmitting,
    fetchedPhotosTotal: fetchedPhotos?.length ?? 0,
  };
};

export default useAccountDownloadsConfigurator;
